import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack
} from '@mui/material'
import React from 'react'
import { ReactQueryDevtools } from 'react-query/devtools'
import { Link } from 'react-router-dom'

import Icon from 'components/Icon'
import { DrawerGremlinLogoLink } from 'components/navigation/Nav/DrawerGremlinLogoLink'
import { DrawerUserInfo } from 'components/navigation/Nav/DrawerUserInfo'
import { navItemConfig } from 'components/navigation/Nav/useNavigation'
import { colors } from 'mui-theme/theme'
import theme from 'theme'
import { UserSessionInfo } from 'types/api/user/session'

const wideDrawerWidth = 200
const narrowDrawerWidth = 80

const navBackgroundColor = colors.primary[900]

export const NavDrawer: React.FC<{
  children: React.ReactNode
  companyDisplayName: string
  currentTeamName: string
  hasFaultInjection: boolean
  navigationConfigItems: navItemConfig[]
  session: UserSessionInfo
}> = ({
  children,
  companyDisplayName,
  currentTeamName,
  hasFaultInjection,
  navigationConfigItems,
  session
}) => {
  const enabledNavigationConfigItems = navigationConfigItems.filter(
    (navItemConfig) => navItemConfig.enabled
  )
  const breakpoint = theme.mq[1]

  return (
    <Box
      sx={{
        display: 'flex'
      }}
    >
      <nav
        aria-label="main-navigation"
        css={{
          background: colors.primary[900],
          zIndex: theme.zIndices[5]
        }}
      >
        <Drawer
          css={[
            {
              width: narrowDrawerWidth,
              flexShrink: 0,
              whiteSpace: 'normal',
              boxSizing: 'border-box',
              transition: 'width 1s',

              [breakpoint]: {
                width: wideDrawerWidth,
                transition: 'width 1s'
              }
            }
          ]}
          open={true}
          sx={{
            '& .MuiDrawer-paper': {
              background: navBackgroundColor,
              width: narrowDrawerWidth,
              overflowX: 'hidden',
              transition: 'width 1s',

              [breakpoint]: {
                width: wideDrawerWidth,
                transition: 'width 1s'
              }
            }
          }}
          variant="permanent"
        >
          <Stack
            css={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto'
            }}
          >
            <DrawerGremlinLogoLink
              url={hasFaultInjection ? '/' : '/services'}
            />
            <List
              css={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto'
              }}
            >
              {enabledNavigationConfigItems.map((navItem) => (
                <ListItem key={navItem.text} disablePadding>
                  <ListItemButton
                    component={Link}
                    css={[
                      {
                        cursor: navItem.disabled ? 'not-allowed' : 'pointer',
                        color: navItem.disabled
                          ? theme.colors.steel[1]
                          : theme.colors.white[0],
                        flexDirection: 'column',
                        justifyContent: 'center',
                        paddingTop: theme.space[3],
                        paddingBottom: theme.space[3],
                        paddingLeft: theme.space[1],
                        paddingRight: theme.space[1],
                        svg: {
                          opacity: 0.5,
                          color: theme.colors.white[0]
                        },

                        [breakpoint]: {
                          flexDirection: 'row',
                          justifyContent: 'initial'
                        },

                        '&:hover, &:focus': {
                          color: navItem.disabled
                            ? theme.colors.steel[1]
                            : theme.colors.white[0],
                          svg: {
                            opacity: 1,
                            color: colors.secondary.blue[100]
                          }
                        },
                        ...navItem.style
                      },

                      navItem.showNotification && {
                        position: 'relative',
                        '&::before': {
                          content: "''",
                          position: 'absolute',
                          top: theme.space[2],
                          right: theme.space[3],
                          width: theme.space[2],
                          height: theme.space[2],
                          borderRadius: theme.borderRadius[2],
                          background: theme.colors.yellow[0],
                          border: `2px solid ${theme.colors.steel[2]}`,
                          zIndex: 2
                        }
                      },

                      navItem.isActive && {
                        color: theme.colors.white[0],
                        background: colors.primary[650],
                        svg: {
                          opacity: 1,
                          color: theme.colors.white[0]
                        },

                        // this is needed to override the mui styling defaults
                        '&:hover, &:focus': {
                          background: colors.primary[650]
                        },

                        '&:hover': {
                          svg: {
                            color: colors.secondary.blue[100]
                          }
                        }
                      }
                    ]}
                    data-link-active={navItem.isActive}
                    data-testid={`nav-${navItem.name}`}
                    data-track-action={`open-${navItem.name}`}
                    data-track-category="main-navigation"
                    data-track-on="click"
                    disabled={navItem.disabled}
                    to={navItem.route}
                    {...(navItem.props || {})}
                  >
                    <ListItemIcon
                      sx={{
                        justifyContent: 'center',
                        color: navItem.disabled
                          ? theme.colors.steel[1]
                          : theme.colors.white[0]
                      }}
                    >
                      <Icon
                        css={[
                          {
                            width: theme.fontSizes[4],
                            height: theme.fontSizes[4],
                            transition: 'opacity 150ms ease 0s'
                          }
                        ]}
                        data-test-navigation-notification={
                          navItem.showNotification
                        }
                        icon={navItem.icon}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={navItem.text}
                      primaryTypographyProps={{ variant: 'body2' }}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: theme.fontWeights[0],
                          lineHeight: `${theme.space[3]}px`,
                          textAlign: 'center',
                          fontSize: theme.fontSizes[0],

                          [breakpoint]: {
                            textAlign: 'left',
                            fontSize: theme.fontSizes[1]
                          }
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {process.env.NODE_ENV === 'development' && !window.Cypress && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}

            <DrawerUserInfo
              companyDisplayName={companyDisplayName}
              css={{
                display: 'none',
                [breakpoint]: {
                  display: 'unset'
                }
              }}
              currentTeamName={currentTeamName}
              session={session}
            />
          </Stack>
        </Drawer>
      </nav>
      {children}
    </Box>
  )
}
